import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {Editor} from 'grapesjs';
import type {ButtonAppearance} from '@Components/button';
import {Button, IconPlacement, ButtonSize, ButtonType, UPSELL_BUTTON_APPEARANCE} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {isUserPremium} from '@Libraries/user.library';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import {saveAndPublish} from '@EmailMakerReact/lib/email-maker-publish-library';
import {findLinkComponentByHref, selectAndRevealComponent, subscribeToRevealInvalidLink} from '@EmailMakerReact/lib/email-maker-selection-library';
import {EmailMakerSaveConflictPopover} from '../email-maker-save-conflict-popover/email-maker-save-conflict-popover';
import {EmailMakerSaveErrorPopover} from '../email-maker-save-error-popover/email-maker-save-error-popover';
import {EmailMakerVersionHistoryOnboardingPopover} from '../email-maker-version-history-onboarding-popover/email-maker-version-history-onboarding-popover';
import {hasDisplayableSaveError} from '../email-maker-save-error-popover/email-maker-save-error-popover.types';
import {SaveStatus} from '../save-status/save-status';
import {UndoRedoButtons} from '../undo-redo-buttons/undo-redo-buttons';
import {PreviewButton} from '../preview-button/preview-button';
import {FileMenu} from '../file-menu/file-menu';
import {HelpMenu} from '../help-menu/help-menu';
import {ExportMenu} from '../export-menu/export-menu';
import {ShareButton} from '../share-button/share-button';
import {NavLoadingSkeleton} from '../nav-loading-skeleton/nav-loading-skeleton';
import {PublishingOptionsMenu} from '../publishing-options-menu/publishing-options-menu';
import type {NavActionsParams} from '../../email-maker-nav.types';
import {ICON_PUBLISH_ARROW, PUBLISH_HOVER_ANIMATION_CLASS} from '../../email-maker-nav.types';
import styles from '../../email-maker-nav.module.scss';

function renderDesktopOnly(editorViewport: EditorViewport, slotClass: string, child: ReactElement): ReactElement | null {
  if (editorViewport !== EditorViewport.DESKTOP) {
    return null;
  }
  return <div className={slotClass}>{child}</div>;
}

function renderPreviewSlotOnNonMobile(editorViewport: EditorViewport, editor: Editor | null): ReactElement | null {
  if (editorViewport === EditorViewport.MOBILE) {
    return null;
  }
  return (
    <div className={styles.previewSlot}>
      <PreviewButton editorViewport={editorViewport} editor={editor} />
    </div>
  );
}

function renderPublishIcon(onClick: () => void): ReactElement {
  return (
    <Icon
      icon={ICON_PUBLISH_ARROW}
      onClick={onClick}
      isUsedAsButton
      ariaLabel={window.i18next.t('pmwjs_send')}
      type={isUserPremium() ? IconType.SECONDARY : IconType.PREMIUM}
      shape={IconShape.SQUARE}
      size={IconSize.SIZE_ICON_20}
      className={styles.publishIconButton}
    />
  );
}

function renderPublishCta(editor: Editor | null, editorViewport: EditorViewport): ReactElement {
  function handlePublishClick(): void {
    if (editor) {
      void saveAndPublish(editor);
    }
  }

  if (editorViewport === EditorViewport.MOBILE) {
    return renderPublishIcon(handlePublishClick);
  }

  const getSendButtonAppearance = (): ButtonAppearance => {
    if (isUserPremium()) {
      return {type: ButtonType.TERTIARY};
    }
    return UPSELL_BUTTON_APPEARANCE;
  };

  return (
    <div className={styles.publishButtonWrapper}>
      <Button
        {...getSendButtonAppearance()}
        size={ButtonSize.MEDIUM}
        text={window.i18next.t('pmwjs_send')}
        icon={ICON_PUBLISH_ARROW}
        iconPlacement={IconPlacement.RIGHT}
        customClasses={PUBLISH_HOVER_ANIMATION_CLASS}
        onClick={handlePublishClick}
      />
    </div>
  );
}

export function NavActions({isEditorLoading, editorViewport, editor, actions, canUndo, canRedo, saveError}: NavActionsParams): ReactElement {
  const [isSaveErrorPopoverOpen, setIsSaveErrorPopoverOpen] = useState(false);
  const isSaveErrorDisplayable = hasDisplayableSaveError(saveError);

  useEffect(() => {
    setIsSaveErrorPopoverOpen(isSaveErrorDisplayable);
  }, [isSaveErrorDisplayable]);

  // Always mounted with the editor; handles reveal requests from both popover and phone modal.
  useEffect(() => {
    if (!editor) {
      return undefined;
    }

    return subscribeToRevealInvalidLink((href) => {
      const link = findLinkComponentByHref(editor, href);
      if (link) {
        selectAndRevealComponent(editor, link);
      }
    });
  }, [editor]);

  if (isEditorLoading) {
    return <NavLoadingSkeleton editorViewport={editorViewport} />;
  }

  function toggleSaveErrorPopover(): void {
    setIsSaveErrorPopoverOpen((wasOpen) => !wasOpen);
  }

  function closeSaveErrorPopover(): void {
    setIsSaveErrorPopoverOpen(false);
  }

  return (
    <>
      <div className={styles.leftActions}>
        {renderDesktopOnly(editorViewport, styles.menuSlot, <FileMenu />)}
        <div className={styles.undoRedoSlot}>
          <UndoRedoButtons actions={actions} canUndo={canUndo} canRedo={canRedo} />
        </div>
      </div>

      <div className={styles.nameRegion} />

      <div className={styles.right}>
        <div className={styles.statusSlot}>
          <SaveStatus onErrorIndicatorClick={toggleSaveErrorPopover} />
          <EmailMakerSaveConflictPopover editor={editor} saveError={saveError} />
          <EmailMakerSaveErrorPopover saveError={saveError} isOpen={isSaveErrorPopoverOpen} onClose={closeSaveErrorPopover} />
          <EmailMakerVersionHistoryOnboardingPopover />
        </div>

        {renderDesktopOnly(editorViewport, styles.divider, <></>)}
        {renderDesktopOnly(editorViewport, styles.helpSlot, <HelpMenu />)}
        {renderPreviewSlotOnNonMobile(editorViewport, editor)}
        {renderDesktopOnly(editorViewport, styles.shareSlot, <ShareButton />)}
        {renderDesktopOnly(editorViewport, styles.exportSlot, <ExportMenu />)}
        {renderDesktopOnly(editorViewport, styles.deviceSwitcherSlot, <></>)}
        {renderPublishCta(editor, editorViewport)}
        <PublishingOptionsMenu editorViewport={editorViewport} editor={editor} />
      </div>
    </>
  );
}
